import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Container from "../ui/Container";
import MagneticButton from "../ui/MagneticButton";
import TextReveal from "../animations/TextReveal";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

export default function Hero() {
  const reduced = usePrefersReducedMotion();
  const gridRef = useRef(null);
  const [scrollHint, setScrollHint] = useState(true);

  useEffect(() => {
    if (reduced || !gridRef.current) return undefined;

    const onMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 16;
      const y = (e.clientY / window.innerHeight - 0.5) * 12;
      gridRef.current.style.transform = `translate(${x}px, ${y}px)`;
    };

    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [reduced]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 40) setScrollHint(false);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="hero"
      className="relative flex min-h-[min(820px,88svh)] w-full flex-col overflow-hidden bg-surface-accent text-neutral-950"
      aria-label="Dexton Technology — software development company"
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-20%,rgba(174,184,255,0.45),transparent)]" aria-hidden />
      <div
        ref={gridRef}
        className="pointer-events-none absolute inset-[-20px] bg-grid-brand opacity-[0.35] transition-transform duration-700 ease-out will-change-transform"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute right-[-10%] top-[10%] h-[420px] w-[420px] rounded-full bg-primary-300/25 blur-[100px]"
        aria-hidden
      />

      <Container className="relative z-10 flex flex-1 flex-col justify-center pb-12 pt-24 sm:pb-14 sm:pt-28 lg:pb-16 lg:pt-32">
        <p className="mb-6 text-[11px] font-bold uppercase tracking-[0.32em] text-primary-700">
          Digital product engineering
        </p>

        <h1 className="sr-only">
          Software Development Company Building Scalable Digital Products — Dexton Technology
        </h1>

        <TextReveal
          lines={["We build software", "businesses depend on."]}
          className="font-display max-w-[14ch] text-[clamp(2.75rem,8vw,5.5rem)] font-bold leading-[0.95] tracking-tight"
          lineClassName="text-neutral-950 [&:last-child]:text-primary-700"
          delay={0.15}
        />

        <p className="mt-8 max-w-xl text-base leading-relaxed text-neutral-600 sm:text-lg md:mt-10">
          Dexton Technology designs and develops scalable mobile apps, web platforms, and custom software for
          startups, enterprises, and international teams ready to grow.
        </p>

        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <MagneticButton to="/get-proposal">Start a Project</MagneticButton>
          <MagneticButton to="/portfolio" variant="secondary">
            Explore Our Work
          </MagneticButton>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 border-t border-neutral-200/80 pt-6 text-sm text-neutral-500">
          <span>Mobile · Web · Enterprise</span>
          <span>React Native · Next.js · Cloud</span>
          <Link to="/hire-developers" className="font-medium text-primary-700 hover:text-primary-800">
            Hire dedicated developers →
          </Link>
        </div>
      </Container>

      {scrollHint ? (
        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-400">
          <span className="h-8 w-px animate-pulse bg-neutral-300" aria-hidden />
          Scroll to explore
        </div>
      ) : null}
    </section>
  );
}
